import DealAPI from 'dashboard/api/deals';
import { createStore } from 'dashboard/store/helpers/storeFactory';
import camelcaseKeys from 'camelcase-keys';

export default createStore({
  name: 'Deal',
  API: DealAPI,
  getters: {
    getDealsList: state => {
      return camelcaseKeys(state.records, { deep: true });
    },
    getDealById: state => id => {
      const deal = state.records.find(d => d.id === Number(id));
      return deal ? camelcaseKeys(deal, { deep: true }) : null;
    },
    getDealsMeta: state => {
      return state.meta || { count: 0, current_page: 1, total_pages: 1 };
    },
    getUIFlags: state => {
      return state.uiFlags || {};
    },
    getStats: state => {
      return state.stats || {};
    },
    getOpenDeals: state => {
      return state.records.filter(d => d.status === 'open');
    },
    getWonDeals: state => {
      return state.records.filter(d => d.status === 'won');
    },
    getLostDeals: state => {
      return state.records.filter(d => d.status === 'lost');
    },
  },
  mutations: {
    SET_STATS(state, stats) {
      state.stats = stats;
    },
  },
  actions: mutationTypes => ({
    fetchFiltered: async ({ commit }, filters = {}) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await DealAPI.get(filters);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    search: async ({ commit }, { query, page }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await DealAPI.search(query, page);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    getDeal: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { fetching: true });
      try {
        const { data } = await DealAPI.getDeal(id);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetching: false });
      }
    },
    createDeal: async ({ commit }, dealData) => {
      commit(mutationTypes.SET_UI_FLAG, { creating: true });
      try {
        const { data } = await DealAPI.create(dealData);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { creating: false });
      }
    },
    updateDeal: async ({ commit }, { id, ...dealData }) => {
      commit(mutationTypes.SET_UI_FLAG, { updating: true });
      try {
        const { data } = await DealAPI.update(id, dealData);
        commit(mutationTypes.EDIT, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { updating: false });
      }
    },
    deleteDeal: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { deleting: true });
      try {
        await DealAPI.delete(id);
        commit(mutationTypes.DESTROY, id);
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { deleting: false });
      }
    },
    fetchStats: async ({ commit }) => {
      const { data } = await DealAPI.getStats();
      commit('SET_STATS', data);
      return data;
    },
    markWon: async ({ commit }, id) => {
      const { data } = await DealAPI.markWon(id);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    markLost: async ({ commit }, { id, lossReason, lossNotes }) => {
      const { data } = await DealAPI.markLost(id, lossReason, lossNotes);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    reopen: async ({ commit }, id) => {
      const { data } = await DealAPI.reopen(id);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    moveStage: async ({ commit }, { id, stage, probability }) => {
      const { data } = await DealAPI.moveStage(id, stage, probability);
      commit(mutationTypes.EDIT, data);
      return data;
    },
  }),
});
